import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { FileDown, Hourglass, Loader2, LogOut, Receipt, ShieldCheck, Lock, Scale, Radar } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

export default function Account() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [gdprRequests, setGdprRequests] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => {
    if (location.state?.user) return;
    const checkAuth = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(data);
        setIsAuthenticated(true);
      } catch (e) {
        setIsAuthenticated(false);
        navigate("/");
      }
    };
    checkAuth();
  }, [location.state, navigate]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/gdpr/requests`, { withCredentials: true }).then((r) => setGdprRequests(r.data.requests)).catch(() => setGdprRequests([]));
    axios.get(`${API}/me/invoices`, { withCredentials: true }).then((r) => setInvoices(r.data.invoices)).catch(() => setInvoices([]));
  }, [user]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // session may already be gone
    }
    navigate("/");
    window.location.reload();
  };

  const downloadReport = async () => {
    if (reportLoading) return;
    setReportLoading(true);
    try {
      const { data } = await axios.get(`${API}/me/report`, {
        params: { lang: i18n.language },
        responseType: "blob",
        withCredentials: true,
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wadoo-rapport-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t("report.error"));
    } finally {
      setReportLoading(false);
    }
  };

  const downloadInvoice = async (inv) => {
    if (invoiceLoading) return;
    setInvoiceLoading(inv.id);
    try {
      const { data } = await axios.get(`${API}/me/invoices/${inv.id}/pdf`, {
        responseType: "blob",
        withCredentials: true,
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `facture-wadoo-${inv.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t("invoices.error"));
    } finally {
      setInvoiceLoading(null);
    }
  };

  if (isAuthenticated === null) {
    return (
      <main data-testid="account-loading" className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-mint animate-spin" strokeWidth={1.5} />
      </main>
    );
  }

  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString(i18n.language, { year: "numeric", month: "long", day: "numeric" })
    : null;

  const plans = t("pricing.plans", { returnObjects: true });
  const subPlan = user?.subscription
    ? plans.find((p) => `wadoo_${p.id}` === user.subscription.lookup_key)
    : null;
  const pendingRequests = (gdprRequests || []).filter((r) => r.status === "sent");

  return (
    <main data-testid="account-page">
      <Header />
      <div className="pt-40 pb-32 max-w-xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="rounded-3xl border border-mint/25 bg-gradient-to-br from-mint/10 via-card to-night p-10"
        >
          <div className="flex items-center gap-5">
            {user?.picture ? (
              <img src={user.picture} alt="" data-testid="account-avatar" className="w-16 h-16 rounded-full border border-white/15" referrerPolicy="no-referrer" />
            ) : (
              <span data-testid="account-avatar-fallback" className="w-16 h-16 rounded-full bg-mint/15 border border-mint/25 text-mint text-2xl font-display font-semibold flex items-center justify-center">
                {user?.name?.[0] || "W"}
              </span>
            )}
            <div>
              <h1 data-testid="account-welcome" className="font-display font-semibold text-white text-2xl tracking-tight">
                {t("auth.welcome", { name: user?.name || "" })}
              </h1>
              <p data-testid="account-email" className="text-sm text-white/50 mt-1">{user?.email}</p>
              {memberSince && (
                <p data-testid="account-member-since" className="text-xs text-white/35 mt-1">
                  {t("auth.memberSince", { date: memberSince })}
                </p>
              )}
            </div>
          </div>

          {subPlan && (
            <div data-testid="account-subscription-card" className="mt-8 rounded-2xl border border-mint/30 bg-mint/10 p-6 flex items-center justify-between">
              <div>
                <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-mint/80">{t("auth.currentPlan")}</p>
                <p data-testid="account-plan-name" className="mt-1 font-display font-semibold text-white text-xl">{subPlan.name}</p>
              </div>
              <span className="rounded-full bg-mint px-3 py-1 text-[11px] font-semibold text-night">{t("auth.trialActive")}</span>
            </div>
          )}

          {gdprRequests !== null && gdprRequests.length > 0 && (
            <div data-testid="account-gdpr-pending" className="mt-8 rounded-2xl border border-white/8 bg-night/60 p-6">
              <div className="flex items-center justify-between gap-3">
                <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-white/40">{t("auth.pendingTitle")}</p>
                <Link to="/gdpr" data-testid="account-pending-view-all" className="text-[11px] text-mint hover:underline underline-offset-2">
                  {t("auth.viewRequests")}
                </Link>
              </div>
              {pendingRequests.length === 0 ? (
                <p className="mt-3 text-sm text-white/50">{t("auth.pendingAll")}</p>
              ) : (
                <ul className="mt-4 space-y-2.5">
                  {pendingRequests.slice(0, 4).map((r) => (
                    <li key={r.id} data-testid={`account-pending-${r.id}`} className="flex items-center justify-between gap-3">
                      <span className="text-sm text-white/75 truncate">{r.service_name}</span>
                      {r.days_left != null && (
                        <span
                          className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold ${
                            r.days_left > 7 ? "bg-sky-400/15 text-sky-300" : r.days_left >= 0 ? "bg-amber-400/15 text-amber-300" : "bg-red-400/15 text-red-300"
                          }`}
                        >
                          <Hourglass className="w-3 h-3" strokeWidth={2} />
                          {r.days_left >= 0 ? t("gdpr.daysLeft", { count: r.days_left }) : t("gdpr.overdue", { count: -r.days_left })}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {invoices !== null && invoices.length > 0 && (
            <div data-testid="account-invoices" className="mt-8 rounded-2xl border border-white/8 bg-night/60 p-6">
              <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-white/40">{t("invoices.title")}</p>
              <ul className="mt-4 space-y-2.5">
                {invoices.map((inv) => (
                  <li key={inv.id} data-testid={`account-invoice-${inv.id}`} className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <span className="text-sm text-white/75 truncate block">{inv.number}</span>
                      <span className="text-xs text-white/35">
                        {new Date(inv.created).toLocaleDateString(i18n.language, { year: "numeric", month: "short", day: "numeric" })}
                        {" · "}
                        {new Intl.NumberFormat(i18n.language, { style: "currency", currency: (inv.currency || "eur").toUpperCase() }).format(inv.total)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span
                        data-testid={`account-invoice-status-${inv.id}`}
                        className={`rounded-full px-3 py-1 text-[11px] font-semibold ${inv.status === "paid" ? "bg-mint/15 text-mint" : "bg-amber-400/15 text-amber-300"}`}
                      >
                        {t(`invoices.status.${inv.status}`, { defaultValue: inv.status })}
                      </span>
                      <button
                        onClick={() => downloadInvoice(inv)}
                        disabled={invoiceLoading === inv.id}
                        data-testid={`account-invoice-download-${inv.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1 text-[11px] font-medium text-white/80 transition-colors duration-200 hover:border-mint/50 disabled:opacity-50"
                      >
                        {invoiceLoading === inv.id ? <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2} /> : <Receipt className="w-3 h-3" strokeWidth={1.5} />}
                        {invoiceLoading === inv.id ? t("invoices.downloading") : t("invoices.download")}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-8 rounded-2xl border border-white/8 bg-night/60 p-6 flex items-start gap-4">
            <ShieldCheck className="w-6 h-6 text-mint shrink-0 mt-0.5" strokeWidth={1.5} />
            <p className="text-sm text-white/60 leading-relaxed">{t("auth.accountNote")}</p>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/gdpr"
              data-testid="account-gdpr-link"
              className="inline-flex items-center gap-2 rounded-full bg-mint px-6 py-3 text-sm font-semibold text-night transition-transform duration-200 hover:scale-105"
            >
              <Scale className="w-4 h-4" strokeWidth={2} />
              {t("gdpr.title")}
            </Link>
            <Link
              to="/vault"
              data-testid="account-vault-link"
              className="inline-flex items-center gap-2 rounded-full bg-mint px-6 py-3 text-sm font-semibold text-night transition-transform duration-200 hover:scale-105"
            >
              <Lock className="w-4 h-4" strokeWidth={2} />
              {t("vault.title")}
            </Link>
            <Link
              to="/discover"
              data-testid="account-discover-link"
              className="inline-flex items-center gap-2 rounded-full bg-mint px-6 py-3 text-sm font-semibold text-night transition-transform duration-200 hover:scale-105"
            >
              <Radar className="w-4 h-4" strokeWidth={2} />
              {t("discover.title")}
            </Link>
            <button
              onClick={downloadReport}
              disabled={reportLoading}
              data-testid="account-report-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-white/80 transition-colors duration-200 hover:border-mint/50 disabled:opacity-50"
            >
              {reportLoading ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <FileDown className="w-4 h-4" strokeWidth={1.5} />}
              {reportLoading ? t("report.downloading") : t("report.download")}
            </button>
            <Link
              to="/#pricing"
              data-testid="account-explore-plans"
              className="inline-flex items-center rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-white/80 transition-colors duration-200 hover:border-mint/50"
            >
              {t("auth.explorePlans")}
            </Link>
            <button
              onClick={logout}
              data-testid="account-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-white/75 transition-colors duration-200 hover:border-red-400/50 hover:text-white"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
              {t("auth.logout")}
            </button>
          </div>
        </motion.div>
      </div>
      <Footer />
    </main>
  );
}
